/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ValidationState } from '@fluentui/react/lib/Pickers';
import { UploadStatus } from '@msteams/services-io-browser-file-upload';
import { Constants } from '../../../../common/Constants';
import { AdobeFileAttachment } from '../../../../models/fileService/AdobeFileAttachment';
import { getAppHost } from '../../../../utilities/TeamsUtils';
import { Recipient } from '../../RecipientList';
import { EsignForm, useCreateEsignEnvelopeForm } from './useCreateEsignEnvelopeForm';
import { InputState } from './useInputState';

const titleValidation = (value: string): boolean => {
    return value.trim().length > 0;
};

const filesValidation = (value: AdobeFileAttachment[]): boolean => {
    return (
        value &&
        value.length > 0 &&
        value.filter(
            (file: AdobeFileAttachment): boolean =>
                file.status !== UploadStatus.COMPLETE || (!file.transientDocumentId && !file.libraryDocumentId)
        ).length === 0
    );
};

const recipientsValidation = (value: Recipient[]): boolean => {
    return (
        value &&
        value.length > 0 &&
        value.every(
            (recipient: Recipient): boolean =>
                recipient.persona &&
                recipient.persona.ValidationState !== ValidationState.invalid &&
                recipient.persona.ValidationState !== ValidationState.warning &&
                !!recipient.persona.email
        )
    );
};

export interface CreateAdobeEnvelopeFormInitialValues {
    title: string;
    setTitle: (value: string) => void;
    details: string;
    setDetails: (value: string) => void;
}

export interface UseCreateAdobeEnvelopeForm extends EsignForm {
    filesInput: InputState<AdobeFileAttachment[]>;
}

export const useCreateAdobeEnvelopeForm = (
    initialValues: CreateAdobeEnvelopeFormInitialValues
): UseCreateAdobeEnvelopeForm => {
    const loc = getAppHost().getLocale();

    return useCreateEsignEnvelopeForm({
        titleInputProps: {
            initialValue: initialValues?.title || '',
            setValue: initialValues.setTitle,
            validation: titleValidation,
            maxLength: Constants.maxTitleCharacterLimit,
        },
        detailsInputProps: {
            initialValue: initialValues.details || '',
            setValue: initialValues.setDetails,
            maxLength: Constants.maxCreateDetailsCharacterLimit,
        },
        filesInputProps: {
            initialValue: [],
            validation: filesValidation,
        },
        recipientsInputProps: {
            initialValue: [],
            validation: recipientsValidation,
            maxLength: Constants.MAX_NUMBER_OF_RECIPIENTS,
        },
        completeInOrderInputProps: {
            initialValue: true,
        },
        availableRoles: [loc.strings.adobe_role_signer, loc.strings.adobe_role_approver],
    });
};
